import React, { useMemo, useState } from 'react';
import { makeStyles, Tabs, Tab } from '@material-ui/core';
import Typography from '../../../components/overrides/Typography';
import {
  filledIconSmallLib,
  iconSmallLib,
} from '../../../utils/constants/VenueLabels';
import clsx from 'clsx';
import { useViewport } from '../../../utils/useViewport';
import { trackClickWithDescription } from '../../../utils/useOneTag';

const useStyles = makeStyles((theme) => ({
  tabLabel: {
    textTransform: 'capitalize',
    '& span': {
      display: 'block',
      textAlign: 'left',
    },
    [theme.breakpoints.up('xxl')]: {
      '& span': {
        display: 'inline',
      },
    },
  },
  tabs: {
    gap: theme.spacing(2.25),
    [theme.breakpoints.up('xxl')]: {
      gap: theme.spacing(4),
    },
  },
  blue: {
    color: theme.palette.spoon.blue[40],
  },
  tab: {
    minWidth: '1px',
    padding: theme.spacing(0, 0),
    margin: theme.spacing(0, 0),
    '&.Mui-selected .MuiTab-wrapper': {
      color: theme.palette.interactionBlue.main,
      outlineColor: theme.palette.interactionBlue.main
    },
    '&:focus-visible': {
      outline: 'solid',
      outlineColor: theme.palette.interactionBlue.main,
      outlineOffset: theme.spacing(0.5),
    }
  },
  tabLayout: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing(1),
    [theme.breakpoints.up('xxl')]: {
      flexDirection: 'column',
    }
  },
  centerTabs: {
    justifyContent: 'center',
  },
  fillerTab: {
    minWidth: 'auto',
    width: theme.spacing(1),
    padding: 0, 
  },
}));

const wrapWordsInSpan = (text) => {
  return text.split(' ').map((word, index) => <span key={index}>{word} </span>);
};

const CuratedCarousel = ({ curatedLabels, activeLabel, onLabelSelection }) => {
  const activeIndex = curatedLabels.indexOf(activeLabel);
  const [value, setValue] = useState(activeIndex === -1 ? false : activeIndex);
  const classes = useStyles();
  const { width: screenWidth } = useViewport();
  const [measuredCarouselWidth, setMeasuredCarouselWidth] = useState(0);
  const isTouchEnabled =
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    navigator.msMaxTouchPoints > 0;

  const isCarouselWiderThanViewport = useMemo(() => {
    return measuredCarouselWidth > screenWidth;
  }, [measuredCarouselWidth, screenWidth]);

  const carouselRef = (node) => {
    setMeasuredCarouselWidth(
      node?.querySelector('.MuiTabs-scroller').scrollWidth
    );
  };

  const handleTabClick = (_, newValue) => {
    if (newValue === value) {
      trackClickWithDescription(
        'search-revamp-curated-picks',
        'Button',
        'unselect ' + curatedLabels[newValue]
      );
      setValue(false);
    } else {
      trackClickWithDescription(
        'search-revamp-curated-picks',
        'Button',
        'select ' + curatedLabels[newValue]
      );
      setValue(newValue);
    }
    const existingLabel = value ? curatedLabels[value] : undefined;
    const newLabel = newValue === value ? undefined : curatedLabels[newValue];
    onLabelSelection(existingLabel, newLabel);
  };

  return (
    <div className={classes.root}>
      <Tabs
        classes={{
          flexContainer: clsx(classes.tabs, {
            [classes.centerTabs]: !isCarouselWiderThanViewport,
          }),
        }}
        value={value}
        onChange={handleTabClick}
        TabIndicatorProps={{ style: { display: 'none' } }}
        variant={isCarouselWiderThanViewport ? 'scrollable' : 'standard'}
        scrollButtons={isTouchEnabled ? 'off' : 'auto'}
        ref={carouselRef}>
        {isCarouselWiderThanViewport && isTouchEnabled && (
          <Tab disabled classes={{ root: classes.fillerTab }} />
        )}
        {curatedLabels.map((key, index) => (
          <Tab
            classes={{ root: classes.tab }}
            disableRipple={true}
            key={key}
            tabIndex={0}
            value={index}
            label={
              <div className={classes.tabLayout}>
                <div style={{ height: '24px' }}>
                  {index === value
                    ? filledIconSmallLib[key]?.image
                    : iconSmallLib[key]?.image}
                </div>
                <Typography
                  variant="small4Semibold"
                  className={clsx(classes.tabLabel, {
                    [classes.blue]: index === value,
                  })}>
                  {wrapWordsInSpan(filledIconSmallLib[key]?.name)}
                </Typography>
              </div>
            }
          />
        ))}
        {isCarouselWiderThanViewport && isTouchEnabled && (
          <Tab disabled classes={{ root: classes.fillerTab }} />
        )}
      </Tabs>
    </div>
  );
};

export default CuratedCarousel;
